import type {
  CellStatus,
  MonthlyCell,
  MonthlyRow,
  MonthlyRowSummary,
} from "@/lib/types";
import { getMonthlyDaySessions } from "@/lib/monthly";

export type PresenceDay = {
  status: CellStatus;
  previous_status?: string | null;
  late_minutes?: number | null;
};

/**
 * Presence % (weekly + monthly tables / PDF reports).
 *
 * Present (numerator): P only
 * In total, not present: A, L — including MS/FS days that were late or absent
 * Removed from total: O, S, TP (also H, pending)
 *
 * MS/FS is a chart label only. Submitting mantras or paying a fine does not
 * excuse lateness or absence.
 */
export function originalChargeStatus(
  status: CellStatus,
  previous?: string | null,
  lateMinutes?: number | null
): CellStatus {
  if (status !== "MS" && status !== "FS") return status;
  if (previous === "L" || previous === "A") return previous;
  return lateMinutes ? "L" : "A";
}

export function computePresencePct(days: PresenceDay[]): {
  present: number;
  late: number;
  absent: number;
  on_leave: number;
  sick: number;
  taken_permission: number;
  mantra_submitted: number;
  fine_submitted: number;
  holiday: number;
  pending: number;
  accountable_days: number;
  present_pct: number | null;
  /** When every day is O / S / TP (nothing accountable). */
  all_out: boolean;
} {
  let present = 0;
  let late = 0;
  let absent = 0;
  let on_leave = 0;
  let sick = 0;
  let taken_permission = 0;
  let mantra_submitted = 0;
  let fine_submitted = 0;
  let holiday = 0;
  let pending = 0;

  for (const day of days) {
    switch (day.status) {
      case "O":
        on_leave += 1;
        break;
      case "S":
        sick += 1;
        break;
      case "TP":
        taken_permission += 1;
        break;
      case "MS":
        mantra_submitted += 1;
        break;
      case "FS":
        fine_submitted += 1;
        break;
      case "H":
        holiday += 1;
        break;
      case "pending":
        pending += 1;
        break;
    }

    switch (originalChargeStatus(day.status, day.previous_status, day.late_minutes)) {
      case "P":
        present += 1;
        break;
      case "L":
        late += 1;
        break;
      case "A":
        absent += 1;
        break;
    }
  }

  const accountable_days = present + late + absent;
  const present_pct =
    accountable_days > 0
      ? Math.round((present / accountable_days) * 10000) / 100
      : null;
  const all_out =
    accountable_days === 0 &&
    (on_leave > 0 || sick > 0 || taken_permission > 0);

  return {
    present,
    late,
    absent,
    on_leave,
    sick,
    taken_permission,
    mantra_submitted,
    fine_submitted,
    holiday,
    pending,
    accountable_days,
    present_pct,
    all_out,
  };
}

/** One status per day for % (first session if multi-shift). */
export function dayStatusFromMonthlyCell(
  cell: MonthlyCell | undefined
): CellStatus | null {
  return dayPresenceFromMonthlyCell(cell)?.status ?? null;
}

export function dayPresenceFromMonthlyCell(
  cell: MonthlyCell | undefined
): PresenceDay | null {
  if (!cell) return null;
  const sessions = getMonthlyDaySessions(cell);
  if (sessions.length === 0) {
    return {
      status: cell.status,
      previous_status: cell.previous_status,
      late_minutes: cell.late_minutes,
    };
  }
  const first = sessions[0];
  return {
    status: first.status,
    previous_status: first.previous_status ?? cell.previous_status,
    late_minutes: first.late_minutes ?? cell.late_minutes,
  };
}

export function summarizeMonthlyRow(
  row: Pick<MonthlyRow, "cells">,
  dates: string[]
): MonthlyRowSummary & { all_out: boolean } {
  const days: PresenceDay[] = [];
  for (const d of dates) {
    const day = dayPresenceFromMonthlyCell(row.cells[d]);
    if (day) days.push(day);
  }
  const s = computePresencePct(days);
  return {
    present: s.present,
    late: s.late,
    absent: s.absent,
    on_leave: s.on_leave,
    sick: s.sick,
    taken_permission: s.taken_permission,
    mantra_submitted: s.mantra_submitted,
    fine_submitted: s.fine_submitted,
    holiday: s.holiday,
    pending: s.pending,
    accountable_days: s.accountable_days,
    present_pct: s.present_pct,
    absent_pct:
      s.accountable_days > 0
        ? Math.round((s.absent / s.accountable_days) * 10000) / 100
        : null,
    all_out: s.all_out,
  };
}

export function formatPresencePct(
  pct: number | null,
  allOut: boolean
): string {
  if (allOut) return "—";
  if (pct == null) return "—";
  return `${pct.toFixed(2)}%`;
}
